/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo, useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Briefcase, Users, Calendar, ChevronDown, Filter, Sparkles, ArrowRight, UserCheck, UserX, Clock, MapPin, Award } from 'lucide-react';
import { motion } from 'motion/react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import dashboardHeroBg from '../../assets/images/dashboard_hero_bg_1780864511918.png';
import { HeroBanner } from '../Common/HeroBanner';
import { formatTime12HourEST } from '../../utils/timeUtils';

import { doesRecordBelongToOrg } from '../../utils/orgTeamUtils';

export const OrganizationDashboard: React.FC = () => {
  const {
    activeUser,
    activeTenant,
    opportunities,
    applications,
    placements,
    users,
    setCurrentView,
    interviews,
    selectedTerm
  } = useWBL();

  const organizationName = activeUser?.orgName || 'Apex Tech Solutions';

  const orgRegions = useMemo(() => {
    const regs = new Set<string>();
    if (activeUser?.tenantId) regs.add(activeUser.tenantId.toLowerCase().trim());
    if (Array.isArray(activeUser?.extendedRegions)) {
      activeUser.extendedRegions.forEach(r => { if (r) regs.add(r.toLowerCase().trim()); });
    }
    // Also include any region where this organization has opportunities
    (opportunities || []).forEach(o => {
      const oName = (o.organizationName || (o as any).orgName || '').toLowerCase().trim();
      const myName = organizationName.toLowerCase().trim();
      if (oName && myName && (oName === myName || oName.includes(myName) || myName.includes(oName) || oName === 'apex tech solutions')) {
        if (o.tenantId) regs.add(o.tenantId.toLowerCase().trim());
      }
    });
    return regs;
  }, [activeUser, opportunities, organizationName]);

  const orgMatchFn = (record: { organizationName?: string | null; orgName?: string | null; organizationId?: string | null; postedByUserId?: string | null; }) => {
    const recName = (record.organizationName || record.orgName || '').toLowerCase().trim();
    const myName = (organizationName || '').toLowerCase().trim();
    const belongs = doesRecordBelongToOrg(record, activeUser, users);

    const nameMatch = recName && myName && (recName === myName || recName.includes(myName) || myName.includes(recName) || recName === 'apex tech solutions' || myName === 'apex tech solutions');

    if (!nameMatch && !belongs) {
        console.log("DEBUG: Filtered out record - NameMatch:", nameMatch, "Belongs:", belongs, "RecName:", recName, "MyName:", myName);
    }

    return !!(nameMatch || belongs);
  };

  const tenantMatchFn = (tenantId?: string | null) => {
    console.log("DEBUG: Checking tenant match, activeTenant:", activeTenant?.id, "RecordTenant:", tenantId);
    if (activeTenant?.id === 'global') return true;
    const tId = (tenantId || '').toLowerCase().trim();
    return !tId || tId === activeTenant.id.toLowerCase().trim() || orgRegions.has(tId);
  };

  // Metrics calculation (transcends term and compiles across organization's regions in Global view)
  const myApplications = useMemo(() => {
    let apps = applications || [];
    console.log("DEBUG: Total apps:", apps.length, "ActiveTenant:", activeTenant?.id);
    return apps.filter(a => {
        const matches = orgMatchFn(a) && tenantMatchFn(a.tenantId);
        if (!matches) {
            console.log("DEBUG: Filtered out app:", a.id, "AppOrg:", a.organizationName, "AppTenant:", a.tenantId);
        }
        return matches;
    });
  }, [applications, activeTenant?.id, orgRegions, activeUser, users, organizationName]);
  
  const appliedCount = myApplications.length;
  const approvedCount = myApplications.filter(a => a.status === 'accepted').length;
  const declinedCount = myApplications.filter(a => a.status === 'rejected').length;
  const interviewedCount = myApplications.filter(a => a.status === 'reviewed' || a.interviewScheduled || interviews.some(i => i.applicationId === a.id)).length;

  const myOpportunities = useMemo(() => {
    let opps = opportunities || [];
    return opps.filter(o => orgMatchFn(o) && tenantMatchFn(o.tenantId));
  }, [opportunities, activeTenant?.id, orgRegions, activeUser, users, organizationName]);

  const activeOpportunities = myOpportunities.filter(o => o.status === 'open');

  const totalOpenings = useMemo(() =>
    activeOpportunities.reduce((sum, o) => sum + (o.numOpenings || 1), 0),
    [activeOpportunities]
  );

  // Role data
  const roleCounts = useMemo(() => {
    const counts = { student: 0, organization: 0, mentor: 0, advisor: 0 };
    users.filter(u => u.tenantId === activeTenant.id && u.uid !== activeUser?.uid).forEach(u => {
       if (u.role === 'student') counts.student++;
       else if (u.role === 'organization') counts.organization++;
       else if (u.role === 'mentor') counts.mentor++;
       else if (u.role === 'advisor' || u.role === 'regional_admin') counts.advisor++;
    });
    return counts;
  }, [users, activeTenant.id, activeUser?.uid]);

  // Opportunities breakdown by Career Pathway
  const oDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    myOpportunities.forEach(o => {
      const cluster = o.careerCluster || o.industry || 'Other Sectors';
      counts[cluster] = (counts[cluster] || 0) + 1;
    });

    const colors = ['#0094c8', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#3b82f6', '#14b8a6'];
    const entries = Object.entries(counts);
    
    if (entries.length === 0) {
      return [{ name: 'No Postings', value: 0, color: '#94a3b8' }];
    }

    return entries.map(([name, value], idx) => ({
      name,
      value,
      color: colors[idx % colors.length]
    }));
  }, [myOpportunities]);

  const activeRolesChartData = useMemo(() => {
    return oDistribution;
  }, [oDistribution]);

  const chartData = activeRolesChartData;

  const [showMissingItems, setShowMissingItems] = useState(false);

  // Task List Generation
  const tasks = useMemo(() => {
    const list: any[] = [];
    
    // 1. Pending Applications
    const pendingApps = myApplications.filter(a => a.status === 'submitted' || a.status === 'under_review');
    pendingApps.forEach(app => {
      list.push({
        id: `app-${app.id}`,
        type: 'application',
        title: 'Review Application',
        subtitle: `Applicant: ${app.studentName}`,
        date: app.submittedAt || '',
        status: 'pending decision',
        icon: 'application',
        action: () => setCurrentView('applications'),
        color: '#f59e0b', // amber
      });
    });

    // 2. Upcoming Interviews
    const myInterviews = interviews.filter(i => myApplications.some(a => a.id === i.applicationId));
    myInterviews.forEach(i => {
      const app = myApplications.find(a => a.id === i.applicationId);
      list.push({
        id: `int-${i.id}`,
        type: 'interview',
        title: 'Interview Session',
        subtitle: `Candidate: ${app?.studentName || 'Student'}`,
        date: i.date || '',
        time: i.time ? formatTime12HourEST(i.time) : '',
        status: 'scheduled',
        icon: 'interview',
        action: () => setCurrentView('interviews'),
        color: activeTenant.primaryColor,
      });
    });

    // 3. Pending Evaluations
    const myPlacements = placements.filter(p => p.organizationName === organizationName && p.tenantId === activeTenant.id && p.status === 'active');
    myPlacements.forEach(p => {
      if (!p.eval1 || p.eval1.status === 'pending') {
        list.push({
          id: `eval1-${p.id}`,
          type: 'evaluation',
          title: 'Midterm Evaluation',
          subtitle: `Student: ${p.studentName}`,
          date: '',
          status: 'needs completion',
          icon: 'evaluation',
          action: () => setCurrentView('applications'), 
          color: '#10b981', // emerald
        });
      } else if (!p.eval2 || p.eval2.status === 'pending') {
        list.push({
          id: `eval2-${p.id}`,
          type: 'evaluation',
          title: 'Final Evaluation',
          subtitle: `Student: ${p.studentName}`,
          date: '',
          status: 'needs completion',
          icon: 'evaluation',
          action: () => setCurrentView('applications'),
          color: '#10b981', // emerald
        });
      }
    });
    
    // Sort logic (very basic, interviews first or by date)
    // Here we just return them raw
    return list;
  }, [myApplications, interviews, placements, organizationName, activeTenant.id, setCurrentView, activeTenant.primaryColor]);

  const { readiness, missingItems } = useMemo(() => {
    if (!activeUser) return { readiness: 0, missingItems: [] };

    const criteria = [
      { label: 'Organization Name', value: !!activeUser.orgName },
      { label: 'Main Career Cluster', value: !!activeUser.industry },
      { label: 'Worksite Address', value: !!activeUser.address },
      { label: 'Website URL', value: !!activeUser.website },
      { label: 'Logo Upload', value: !!activeUser.avatar && typeof activeUser.avatar === 'string' && (activeUser.avatar.startsWith('http') || activeUser.avatar.startsWith('https')) },
      { label: 'First Name', value: !!activeUser.contactFirstName },
      { label: 'Last Name', value: !!activeUser.contactLastName },
      { label: 'Email Address', value: !!(activeUser.email || activeUser.contactEmail) },
      { label: 'Phone Number', value: !!activeUser.contactPhone },
      { label: 'Company Profile', value: !!activeUser.companyProfileText },
      { label: 'Onboarding Information', value: !!activeUser.onboardingText },
      { label: 'Targeting Selections', value: !!activeUser.targetingMap && activeUser.targetingMap.trim().length > 0 }
    ];

    const filled = criteria.filter(c => c.value).length;
    const missing = criteria.filter(c => !c.value).map(c => c.label);

    return {
      readiness: Math.floor((filled / criteria.length) * 100),
      missingItems: missing
    };
  }, [activeUser]);

  const COLORS = [activeTenant.primaryColor, activeTenant.secondaryColor, '#64748b', '#94a3b8'];

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <HeroBanner 
        title="DASHBOARD"
        subtitle={`Role: ${activeUser?.role ? activeUser.role.replace('_', ' ') : 'ORGANIZATION'} | Region: ${activeTenant?.name || 'Northeast Region'}`}
        pageKey="dashboard"
      />

      <div className="w-[95%] mx-auto space-y-8 pb-12">
        {readiness < 100 && (
          <div className="flex flex-col gap-4">
              <div 
                  style={{ backgroundColor: `${activeTenant.primaryColor}08`, borderColor: `${activeTenant.primaryColor}33` }} 
                  className="border rounded-3xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-6 group hover:border-opacity-40 transition-all duration-300"
              >
                  <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                              <div 
                                  style={{ borderColor: `${activeTenant.primaryColor}33`, color: activeTenant.primaryColor }} 
                                  className="w-10 h-10 rounded-2xl bg-white border flex items-center justify-center shadow-xs group-hover:scale-110 transition-transform duration-500"
                              >
                                  <Award className="w-5 h-5" />
                              </div>
                              <div>
                                  <h3 style={{ color: activeTenant.primaryColor }} className="text-[10px] font-black uppercase tracking-[0.2em] leading-none mb-1">Profile Readiness</h3>
                                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-tight">Complete your profile to unlock all platform features</p>
                              </div>
                          </div>
                          <div className="flex flex-col items-end">
                              <span style={{ color: activeTenant.primaryColor }} className="text-xl font-black font-mono leading-none">{readiness}%</span>
                              <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.1em] mt-1">COMPLETED</span>
                          </div>
                      </div>
                      <div className="w-full bg-slate-200/50 rounded-full h-2.5 overflow-hidden border border-slate-200/50 p-0.5 backdrop-blur-sm">
                          <div 
                              className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                              style={{ width: `${readiness}%`, backgroundColor: activeTenant.primaryColor }}
                          >
                              <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.2)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.2)_50%,rgba(255,255,255,0.2)_75%,transparent_75%,transparent)] bg-[length:20px_20px] animate-[progress-stripe_2s_linear_infinite]" />
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                          </div>
                      </div>
                      {missingItems.length > 0 && (
                          <button 
                              onClick={() => setShowMissingItems(!showMissingItems)}
                              style={{ color: activeTenant.primaryColor }}
                              className="mt-3 text-[10px] font-black uppercase tracking-widest hover:underline flex items-center gap-1.5 cursor-pointer"
                          >
                              {showMissingItems ? 'Hide Missing Items' : `View ${missingItems.length} Missing Items`}
                              <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${showMissingItems ? 'rotate-180' : ''}`} />
                          </button>
                      )}
                  </div>
                  <button 
                      onClick={() => setCurrentView('profile')}
                      style={{ borderColor: `${activeTenant.primaryColor}4d`, color: activeTenant.primaryColor }}
                      className="px-6 py-3 bg-white border rounded-2xl text-[10px] font-black uppercase tracking-widest hover:text-white hover:shadow-lg transition-all active:scale-95 flex items-center gap-2 cursor-pointer"
                      onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = activeTenant.primaryColor;
                          e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.color = activeTenant.primaryColor;
                      }}
                  >
                      COMPLETE PROFILE
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                  </button>
              </div>

              {showMissingItems && missingItems.length > 0 && (
                  <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm"
                  >
                      <div className="flex items-center gap-2 mb-4">
                          <Filter style={{ color: activeTenant.primaryColor }} className="w-4 h-4" />
                          <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Incomplete Requirements</h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {missingItems.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl transition-all hover:bg-slate-100">
                                  <div style={{ backgroundColor: activeTenant.primaryColor }} className="w-1.5 h-1.5 rounded-full" />
                                  <span className="text-[10px] font-bold text-slate-600 uppercase tracking-tight">{item}</span>
                              </div>
                          ))}
                      </div>
                  </motion.div>
              )}
          </div>
        )}

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Candidate Pipeline */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                          <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Users className="w-5 h-5" /></div>
                          <div>
                              <h3 className="font-extrabold text-sm text-[#0f2d4a] tracking-tight uppercase">Candidate Pipeline</h3>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Recruitment Conversion</p>
                          </div>
                      </div>
                      <button 
                        onClick={() => setCurrentView('applications')}
                        style={{ color: activeTenant.primaryColor }}
                        className="text-[10px] font-black hover:underline uppercase tracking-widest cursor-pointer"
                      >
                        View All
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3.5">
                        <div className="p-3.5 bg-blue-50/20 hover:bg-blue-50/40 border border-blue-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px] group/item">
                            <span className="text-[8.5px] font-black uppercase text-blue-500 tracking-wider ">APPLIED</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-slate-800  tracking-tight">{appliedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-emerald-50/20 hover:bg-emerald-50/40 border border-emerald-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-emerald-600 tracking-wider ">APPROVED</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-emerald-700  tracking-tight">{approvedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-rose-50/25 hover:bg-rose-50/45 border border-rose-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-rose-500 tracking-wider ">DECLINED</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-rose-600  tracking-tight">{declinedCount}</p>
                            </div>
                        </div>
                        <div className="p-3.5 bg-sky-50/20 hover:bg-sky-50/40 border border-sky-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                            <span className="text-[8.5px] font-black uppercase text-sky-600 tracking-wider ">INTERVIEWS</span>
                            <div className="flex justify-between items-baseline mt-2">
                                <p className="text-2xl font-black text-sky-700  tracking-tight">{interviewedCount}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-auto pt-4 border-t border-slate-100">
                    <div className="flex justify-between text-[10.5px] font-bold text-slate-400 mb-2.5 tracking-wider">
                        <span>HIRING OBJECTIVES</span>
                        <span className=" text-[10.5px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">{approvedCount} / {totalOpenings} Placed</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden p-[1px]">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all duration-500 shadow-xs shadow-emerald-500/10" 
                          style={{ width: `${totalOpenings > 0 ? (approvedCount / totalOpenings) * 100 : 0}%` }} 
                        />
                    </div>
                </div>
            </div>
 
            {/* Active Roles / Opportunities Posted */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                <div>
                    <div className="flex items-center space-x-3 mb-4">
                        <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Briefcase className="w-5 h-5" /></div>
                        <div>
                            <h3 className="font-extrabold text-sm text-[#0f2d4a] tracking-tight uppercase">Opportunities Posted</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider  mt-0.5">Career Pathway Breakdown</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        {/* Interactive Chart Core */}
                        <div className="w-1/2 h-[150px] relative flex items-center justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie 
                                        data={chartData} 
                                        innerRadius={48} 
                                        outerRadius={60} 
                                        paddingAngle={4} 
                                        dataKey="value"
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none mt-1">
                                <p className="text-3xl font-black text-slate-800  tracking-tight">
                                    {myOpportunities.length}
                                </p>
                                <p className="text-[8px] uppercase font-black text-slate-400 tracking-widest ">Postings</p>
                            </div>
                        </div>

                        {/* Custom visual legend table */}
                        <div className="w-1/2 flex flex-col gap-1.5 justify-center">
                            {oDistribution.map((item) => (
                                <div key={item.name} className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50/50 hover:bg-slate-50 rounded-lg border border-slate-100 transition-all">
                                    <div className="flex items-center space-x-1.5 min-w-0">
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                                        <span className="text-[9.5px] font-bold text-slate-500 truncate">{item.name}</span>
                                    </div>
                                    <span className=" text-[10px] font-black text-slate-600">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <button 
                  onClick={() => setCurrentView('opportunities')}
                  style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                  className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto cursor-pointer"
                  onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = activeTenant.primaryColor;
                  }}
                  onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`;
                  }}
                >
                  <span>Manage Postings</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>
 
                        {/* Task List */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                <div>
                    <div className="flex items-center space-x-3 mb-6">
                        <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Calendar className="w-5 h-5" /></div>
                        <div>
                            <h3 className="font-extrabold text-sm text-[#0f2d4a] tracking-tight uppercase">Task List</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider  mt-0.5">Needs Your Attention</p>
                        </div>
                    </div>

                    <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
                        {tasks.length > 0 ? tasks.map((task) => (
                          <div key={task.id} onClick={task.action} className="flex items-center gap-3.5 p-3 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 hover:border-slate-200 rounded-xl transition-all duration-300 group/ev cursor-pointer">
                              {/* Icon Widget */}
                              <div className="w-[44px] h-[46px] border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col items-center justify-center flex-shrink-0 bg-white group-hover/ev:border-opacity-80 transition-all" style={{ color: task.color }}>
                                {task.type === 'application' && <UserCheck className="w-5 h-5" />}
                                {task.type === 'interview' && <Calendar className="w-5 h-5" />}
                                {task.type === 'evaluation' && <Award className="w-5 h-5" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                  <h4 className="font-extrabold text-xs text-[#0f2d4a] truncate">{task.title}</h4>
                                  <p className="text-[9.5px] text-slate-400 uppercase tracking-wider font-bold mt-0.5 truncate">{task.subtitle}</p>
                                  <div style={{ color: task.color }} className="flex items-center gap-1.5 mt-1 text-[9px] font-black uppercase tracking-wider">
                                    <Clock style={{ color: task.color }} className="w-3 h-3" />
                                    <span>{task.time || (task.date ? new Date(task.date).toLocaleDateString() : 'Pending')}</span>
                                    <span className="text-slate-300">•</span>
                                    <span className="text-slate-500 lowercase font-normal italic">{task.status}</span>
                                  </div>
                              </div>
                          </div>
                        )) : (
                          <div className="flex flex-col items-center justify-center py-8 text-center">
                            <Sparkles className="w-8 h-8 text-slate-300 mb-3" />
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">No pending tasks</p>
                            <p className="text-[10px] text-slate-400 mt-1">You're all caught up!</p>
                          </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
